import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  resolveRefName,
  type AssigneeLookup
} from '@bliks-io/ui';
import { GitMerge } from 'lucide-react';
import type {
  EntitySuggestion,
  ProcessDraftAssignment,
  ProcessDraftStep
} from '@bliks-io/contracts';
import {
  StepRecordingSelect,
  type StepSelectOption
} from '@/components/processes/StepRecordingSelect';
import { toastApiError } from '@/api/mutations/toast-api-error';

type NodeKind = 'step' | 'assignment';

interface NodeMeta {
  label: string;
  kind: NodeKind;
  actionType?: 'task' | 'decision';
  description?: string;
  stepNumber?: number;
  assignee?: string;
}

interface MergeNodePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** stableStepId of the right-clicked card (the initial merge participant). */
  sourceNodeId: string | null;
  steps: ProcessDraftStep[];
  draftAssignments: ProcessDraftAssignment[];
  entitySuggestions: EntitySuggestion[];
  assigneeLookup: AssigneeLookup;
  /** stableStepId → flow step number, shown in the candidate picker. */
  stepNumberById: Map<string, number>;
  /** Performs the merge (survivor keeps its kind, merged is deleted). */
  onMerge: (survivorStableStepId: string, mergedStableStepId: string) => Promise<void>;
  /** Called after a successful merge with the survivor's stableStepId. */
  onMerged: (survivorStableStepId: string) => void;
}

/**
 * Draft-editor card merge picker: choose a second card to merge the
 * right-clicked card with, choose which one survives, and confirm. The
 * survivor's kind is the result kind, so a cross-kind merge surfaces a
 * result-kind note.
 */
export function MergeNodePickerDialog({
  open,
  onOpenChange,
  sourceNodeId,
  steps,
  draftAssignments,
  entitySuggestions,
  assigneeLookup,
  stepNumberById,
  onMerge,
  onMerged
}: MergeNodePickerDialogProps) {
  const { t } = useTranslation();
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [survivor, setSurvivor] = useState<'source' | 'candidate'>('source');
  const [isMerging, setIsMerging] = useState(false);

  // Proposed name per suggestion id, for assignees still pending approval.
  const suggestionNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of entitySuggestions) map.set(String(s._id), s.payload.name);
    return map;
  }, [entitySuggestions]);

  const labelOf = useCallback(
    (node: ProcessDraftStep | ProcessDraftAssignment): string =>
      node.generatedName || t('processFlow.mergeDialog.unnamedNode'),
    [t]
  );

  // Resolved assignee name (entity, then still-pending suggestion), surfaced on
  // its own line so it never masquerades as the step title.
  const assigneeNameOf = useCallback(
    (node: ProcessDraftStep | ProcessDraftAssignment): string | undefined => {
      const resolved = resolveRefName(node.assignedTo, node.assignedToModel, assigneeLookup);
      if (resolved) return resolved;
      if (node.assignedTo?.type === 'suggestion') {
        return suggestionNameById.get(String(node.assignedTo.id));
      }
      return undefined;
    },
    [assigneeLookup, suggestionNameById]
  );

  // Every card keyed by stableStepId, with its kind and display label.
  const nodeById = useMemo(() => {
    const map = new Map<string, NodeMeta>();
    for (const s of steps) {
      map.set(String(s.stableStepId), {
        label: labelOf(s),
        kind: 'step',
        actionType: s.actionType,
        description: s.generatedDescription ?? undefined,
        stepNumber: stepNumberById.get(String(s.stableStepId)),
        assignee: assigneeNameOf(s)
      });
    }
    for (const a of draftAssignments) {
      map.set(String(a.stableStepId), {
        label: labelOf(a),
        kind: 'assignment',
        description: a.generatedDescription ?? undefined,
        assignee: assigneeNameOf(a)
      });
    }
    return map;
  }, [steps, draftAssignments, labelOf, assigneeNameOf, stepNumberById]);

  const candidateOptions = useMemo<StepSelectOption[]>(() => {
    const options: StepSelectOption[] = [];
    for (const [id, meta] of nodeById) {
      if (id === sourceNodeId) continue;
      options.push({
        value: id,
        label: meta.label,
        description: meta.description,
        actionType: meta.actionType,
        stepNumber: meta.stepNumber,
        metadata: meta.assignee
          ? t('processFlow.mergeDialog.assignedTo', { name: meta.assignee })
          : undefined
      });
    }
    return options;
  }, [nodeById, sourceNodeId, t]);

  const sourceMeta = sourceNodeId ? nodeById.get(sourceNodeId) : undefined;
  const candidateMeta = candidateId ? nodeById.get(candidateId) : undefined;

  const [survivorId, mergedId] =
    survivor === 'source' ? [sourceNodeId, candidateId] : [candidateId, sourceNodeId];
  const survivorKind = survivor === 'source' ? sourceMeta?.kind : candidateMeta?.kind;
  const isCrossKind = !!sourceMeta && !!candidateMeta && sourceMeta.kind !== candidateMeta.kind;

  const resetState = useCallback(() => {
    setCandidateId(null);
    setSurvivor('source');
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) resetState();
      onOpenChange(next);
    },
    [onOpenChange, resetState]
  );

  const handleConfirm = useCallback(async () => {
    if (!survivorId || !mergedId) return;
    setIsMerging(true);
    try {
      await onMerge(survivorId, mergedId);
      onMerged(survivorId);
      handleOpenChange(false);
    } catch (error) {
      toastApiError(error, 'errors.unknown');
    } finally {
      setIsMerging(false);
    }
  }, [survivorId, mergedId, onMerge, onMerged, handleOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(next) => !isMerging && handleOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('processFlow.mergeDialog.title')}</DialogTitle>
          <DialogDescription>{t('processFlow.mergeDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <StepRecordingSelect
            label={t('processFlow.mergeDialog.selectCandidate')}
            options={candidateOptions}
            value={candidateId}
            onValueChange={setCandidateId}
            placeholder={t('processFlow.mergeDialog.selectCandidate')}
            searchPlaceholder={t('processFlow.mergeDialog.searchCandidates')}
            emptyMessage={t('processFlow.mergeDialog.noCandidates')}
            disabled={isMerging}
          />

          {sourceMeta && candidateMeta && (
            <div className="space-y-2">
              <Label>{t('processFlow.mergeDialog.survivorLabel')}</Label>
              <RadioGroup
                value={survivor}
                onValueChange={(v) => setSurvivor(v as 'source' | 'candidate')}
                disabled={isMerging}
              >
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="source" />
                  {t('processFlow.mergeDialog.keepSource', {
                    name: sourceMeta.label
                  })}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="candidate" />
                  {t('processFlow.mergeDialog.keepTarget', {
                    name: candidateMeta.label
                  })}
                </label>
              </RadioGroup>
            </div>
          )}

          {candidateMeta && isCrossKind && survivorKind && (
            <p className="text-sm text-muted-foreground">
              {survivorKind === 'assignment'
                ? t('processFlow.mergeDialog.resultKindAssignment')
                : t('processFlow.mergeDialog.resultKindStep')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isMerging}>
            {t('processFlow.mergeDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isMerging}
            disabled={!candidateId || !survivorId || !mergedId}
          >
            <GitMerge className="mr-2 h-4 w-4" />
            {t('processFlow.mergeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
